import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, defer, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { noTimeoutContext } from '../http/request-timeout';
import {
  GENERIC_ERROR_MESSAGE,
  GozemApiError,
  SESSION_EXPIRED_MESSAGE,
  toGozemApiError,
} from '@shared/utils/http-error.util';
import { Storage } from '../services/storage';
import { AccountStore } from '../stores/account-store';
import { GozemMoneyAccountStore } from '../stores/gozem-money-account.store';
import type {
  WithdrawalMethodsRequest,
  WithdrawalMethodsResponse,
  WithdrawalTokenRequest,
  WithdrawalTokenResponse,
  WithdrawalAuthTxnRequest,
  WithdrawalAuthTxnResponse,
  VerifyPinRequest,
  VerifyPinResponse,
  InitWithdrawalRequest,
  InitWithdrawalResponse,
  InitWithdrawalParams,
} from '../models';

/**
 * WithdrawalApi — HTTP calls for the "Retirer" flow.
 *
 * Five endpoints in order of the dialog steps:
 *  1. POST /api/topup_withdrawal_method          → available withdrawal methods for an amount
 *  2. POST /api/token                            → saved tokens for a processor (token_service=2)
 *  3. POST /api/merchant/gozem_money/auth_txn    → validate, compute fee + withdrawal_id
 *  4. POST /api/gozem_money/verify_pin           → verify encrypted PIN, get authentication_data
 *  5. POST /api/gozem_money/merchant/init_psp_withdraw → execute the withdrawal
 *
 * Auth credentials are read from localStorage via Storage.
 *
 * Every method wraps its payload construction in `defer()` so that a synchronous throw
 * from `basePayload()` surfaces as an error *notification* on the returned stream rather
 * than escaping the caller's `switchMap` projection — the latter bypasses the store's
 * inner `catchError`, permanently tears down the pipeline and leaves the dialog stuck on
 * a loading flag with no request sent.
 */
@Injectable({
  providedIn: 'root',
})
export class WithdrawalApi {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly accountStore = inject(AccountStore);
  private readonly gmAccountStore = inject(GozemMoneyAccountStore);

  // ── 1. Methods ─────────────────────────────────────────────────────────────

  /**
   * Fetch withdrawal methods available for a given amount.
   * Response methods have relative icon paths — prefix with ConfigService.s3BaseUrl().
   */
  getMethods(amount: number): Observable<WithdrawalMethodsResponse> {
    return defer(() => {
      const base = this.basePayload();
      const payload: WithdrawalMethodsRequest = {
        ...base,
        amount,
        operation_type: 'Withdrawal',
      };
      return this.http.post<WithdrawalMethodsResponse>(
        `${environment.apiUrl}/api/topup_withdrawal_method`,
        payload,
      );
    }).pipe(catchError(this.handleError));
  }

  // ── 2. Tokens ──────────────────────────────────────────────────────────────

  /**
   * Fetch saved tokens (payment accounts) for a specific processor.
   * token_service = 2 filters for WITHDRAWAL tokens only
   * (vs token_service = 1 used in the Topup flow).
   */
  getTokens(processorId: string): Observable<WithdrawalTokenResponse> {
    return defer(() => {
      const base = this.basePayload();
      const payload: WithdrawalTokenRequest = {
        ...base,
        processor_id: processorId,
        token_service: 2,
      };
      return this.http.post<WithdrawalTokenResponse>(`${environment.apiUrl}/api/token`, payload);
    }).pipe(catchError(this.handleError));
  }

  // ── 3. Auth transaction ────────────────────────────────────────────────────

  /**
   * Authorize the withdrawal: validates the amount, computes fees,
   * returns available categories and — critically for withdrawal —
   * the withdrawal_id required by init_psp_withdraw.
   *
   * @param amount      - Amount to withdraw
   * @param processorId - ID of the selected processor
   * @param tokenId     - ID of the selected token (_id)
   * @param methodId    - ID of the selected TopupMethod (method.id from topup_withdrawal_method)
   */
  authTxn(
    amount: number,
    processorId: string,
    tokenId: string,
    methodId: string,
  ): Observable<WithdrawalAuthTxnResponse> {
    return defer(() => {
      const base = this.basePayload();
      const payload: WithdrawalAuthTxnRequest = {
        ...base,
        amount,
        processor_id: processorId,
        token_id: tokenId,
        txn_type: 'Withdrawal',
        transaction_method_id: methodId,
      };
      return this.http.post<WithdrawalAuthTxnResponse>(
        `${environment.apiUrl}/api/merchant/gozem_money/auth_txn`,
        payload,
      );
    }).pipe(catchError(this.handleError));
  }

  // ── 4. Verify PIN ──────────────────────────────────────────────────────────

  /**
   * Verify the user's security PIN.
   *
   * The PIN must already be RSA-OAEP encrypted by the time it reaches this method.
   * Encryption is handled upstream by WithdrawalService via PinEncryptionService.
   *
   * On success: returns authentication_data to be forwarded to init_psp_withdraw.
   * On failure: throws an Error — caller (store) tracks attempt count for lockout logic.
   *
   * @param encryptedPin - RSA-OAEP / SHA-256 Base64-encoded encrypted PIN
   * @param withdrawalId - Withdrawal record ID from the auth_txn response
   */
  verifyPin(encryptedPin: string, withdrawalId: string): Observable<VerifyPinResponse> {
    return defer(() => {
      const base = this.basePayload();
      const payload: VerifyPinRequest = {
        ...base,
        pin: encryptedPin,
        withdrawal_id: withdrawalId,
      };
      return this.http.post<VerifyPinResponse>(
        `${environment.apiUrl}/api/gozem_money/verify_pin`,
        payload,
      );
    }).pipe(catchError(this.handleError));
  }

  // ── 5. Init withdrawal (submit) ────────────────────────────────────────────

  /**
   * Execute the withdrawal transaction.
   *
   * Must be called immediately after a successful verify_pin.
   * authentication_data from verify_pin is forwarded verbatim.
   *
   * @param amount          - Amount to withdraw
   * @param processorId     - ID of the selected processor
   * @param tokenId         - ID of the selected token
   * @param categoryId      - User-selected category ID
   * @param fees            - Fee and commission objects from auth_txn
   * @param message         - Optional user note
   * @param authenticationData - Opaque payload from verify_pin
   * @param withdrawalId    - Withdrawal record ID from auth_txn
   */
  initWithdrawal(params: InitWithdrawalParams): Observable<InitWithdrawalResponse> {
    const {
      amount,
      processorId,
      tokenId,
      categoryId,
      fees,
      message,
      authenticationData,
      withdrawalId,
    } = params;
    return defer(() => {
      const base = this.basePayload();
      const payload: InitWithdrawalRequest = {
        ...base,
        amount,
        processor_id: processorId,
        token_id: tokenId,
        txn_type: 'Withdrawal',
        category: categoryId,
        shared_note: message,
        fee: fees.fee,
        commission: fees.commission,
        payment_tags: [],
        payment_references: [],
        authentication_data: authenticationData,
        withdrawal_id: withdrawalId,
      };
      // No client-side timeout: this call moves money, so aborting our wait can
      // never be safe — see `noTimeoutContext`.
      return this.http.post<InitWithdrawalResponse>(
        `${environment.apiUrl}/api/gozem_money/merchant/init_psp_withdraw`,
        payload,
        { context: noTimeoutContext() },
      );
    }).pipe(catchError(this.handleError));
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private basePayload(): {
    merchant_id: string;
    account_id: string;
    merchant_user_id: string;
    user_type: 'merchant';
    user_gm_account_id: string;
  } {
    const userData = this.storage.getUserData();
    if (!userData) {
      // Thrown inside each method's `defer()`, so this reaches the caller as a stream error
      // rather than a synchronous throw. GozemApiError keeps the type contract the PIN flow
      // relies on; `remainingAttempts: null` means `isAuthStageError()` stays false, so this
      // is classified as a failure rather than a wrong-PIN attempt.
      throw new GozemApiError(SESSION_EXPIRED_MESSAGE, 'NO_USER_DATA', null, null);
    }
    return {
      merchant_id: userData.merchantId,
      // Active account, not accounts[0]: pinning to the first row ignores which
      // merchant the user actually selected. See AccountStore.resolveAccountId.
      account_id: this.accountStore.resolveAccountId(),
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      user_gm_account_id: this.gmAccountStore.primaryAccountId(),
    };
  }

  private handleError(error: unknown): Observable<never> {
    // A non-HttpErrorResponse comes from one of our own pre-request guards (basePayload),
    // already shaped as a GozemApiError with user-facing text. `toGozemApiError` only reads
    // HttpErrorResponse fields and would flatten it to the generic fallback — telling the
    // user to retry a call that can never succeed. Forward it verbatim.
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }
    // Preserve business code + remaining_attempts so verify_pin failures keep
    // the backend's attempt count and message (see GozemApiError).
    return throwError(() => toGozemApiError(error, GENERIC_ERROR_MESSAGE));
  }
}
